import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { assignOrEditCustomer } from "@/utils/vue-helpers/customer";
import BookingModule from "@/store/modules/booking";
import POSConfigModule from "@/store/modules/pos-config";
import CashFloatModule from "@/store/modules/cash-float";
import CustomersModule from "@/store/modules/customers";
import AppModule from "@/store/modules/app";
import { BookingStep } from "@/constants/booking";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { useOrderStore } from "@smeetz/order-store";
import { printBookingReceipt } from "@/utils/vue-helpers/booking";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import { getLinkedWaiversCount } from "@/api/waivers/waivers";
import NotificationService from "@/services/notification.service";
import Timeout from "await-timeout";
import { Delete as DeleteIcon } from "@element-plus/icons-vue";
import person from "@/assets/images/svg/person.svg";
import printer from "@/assets/images/svg/printer.svg";
import calculatorSvg from "@/assets/images/svg/calculator-sign.svg";

/**
 * Returns all necessary variables (objects and functions) used in AsideOrderSummary and can be used as well in CustomerBasket
 */
export function setupAsideOrderSummary() {
  const { t } = useI18n({});

  const showCancelTicketsModal = ref(false);
  const isOnline = ref(window.navigator.onLine);
  const isLongTitle = String(t("asideordersummary.title")).length > 15;

  const iconStyle =
    "width: 18px; height: 18px; filter: brightness(0) saturate(100%) contrast(170%) invert(100%);";
  const iconContent = person;
  const iconAlt = "user";

  const printIcon = printer;
  const printAlt = "print";

  const calcIcon = calculatorSvg;
  const calcAlt = "calculator";
  const calcIconStyle = "width: 30px; height: 30px;";

  const calcVisibility = ref(false);

  // computed property used to show removeButton loading if the removal process involves async operations
  const isRemoveButtonLoading = ref(false);

  const showCopyCheck = ref(false);

  ///////////////////////////////////
  const closeCalculator = () => {
    calcVisibility.value = !calcVisibility.value;
    BookingModule.setCalcValue("0");
  };

  const getCustomerFullName = computed<string>(() => {
    const orderStore = useOrderStore();
    const customer = CustomersModule.selectedCustomers.find(
      (c: any) => c.orderId === orderStore.selectedOrder?.id
    );
    if (customer) {
      return customer.fullName;
    }
    return t("asideordersummary.assign-customer");
  });

  const groupCurrencies = computed(() => {
    return POSConfigModule.groupCurrencies;
  });

  const currencies = computed(() => {
    const currencies = [];
    const startAmounts = CashFloatModule.cashFloat?.startAmounts;
    for (const currency in startAmounts) {
      const currencyInfo = groupCurrencies.value.find(
        (c) => c.label === currency
      );
      if (currencyInfo) {
        currencies.push(currencyInfo);
      }
    }
    return currencies;
  });

  const groupCurrency = computed(() => {
    return (
      currencies.value.find((c) => c.isDefault === 1) || currencies.value[0]
    );
  });

  const onClickCustomers = () => {
    assignOrEditCustomer();
  };

  function closeModal() {
    showCancelTicketsModal.value = false;
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      showCopyCheck.value = true;
      setTimeout(() => {
        showCopyCheck.value = false;
      }, 1500);
    });
  }

  function printBill() {
    printBookingReceipt({ isBill: true });
  }

  const showPrintBill = computed(() => {
    const unsupportedCountries = ["FR", "BE"];
    if (unsupportedCountries.includes(POSConfigModule.getGroupCountryCode)) {
      return false;
    }

    const orderMetaStore = useOrderMetaStore();
    if (orderMetaStore.step === BookingStep.CompleteOrder) {
      // Don't allow printing bill in Complete Order step
      return false;
    }

    const orderStore = useOrderStore();
    if (orderStore.selectedOrderSummary.orderLines.length === 0) {
      return false;
    }
    return true;
  });

  // ── Waiver section (CNT-540) ────────────────────────────────────────────────
  // The aside shows a waiver section in the completed-order step for the groups
  // allowed to use waivers.
  const isCompletedBooking = computed(() => {
    const orderMetaStore = useOrderMetaStore();
    return orderMetaStore.step === BookingStep.CompleteOrder;
  });

  const isWaiverAllowed = computed(() => {
    return (
      AppModule.debugMode ||
      POSConfigModule.groupId === 1 ||
      POSConfigModule.groupId === 19066 ||
      POSConfigModule.groupId === 19755 ||
      POSConfigModule.groupId === 19763 ||
      POSConfigModule.groupId === 19786 ||
      import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev"
    );
  });

  // ── Waiver linked count ─────────────────────────────────────────────────────
  const linkedCount = ref(0);
  const isLoadingCount = ref(false);

  async function fetchLinkedWaiversCount() {
    isLoadingCount.value = true;
    try {
      const uuid = BookingModule.selectedBookingUUID;
      let bookingInfo = null;
      let retries = 0;
      while (!bookingInfo && retries < 3) {
        bookingInfo = await getBookingWithUUID(uuid);
        if (!bookingInfo) {
          retries++;
          await Timeout.set(3000);
        }
      }
      if (!bookingInfo?.id) return;
      linkedCount.value = await getLinkedWaiversCount(bookingInfo.id);
    } catch (e) {
      NotificationService.showGeneralResponseError();
    } finally {
      isLoadingCount.value = false;
    }
  }

  function incrementLinkedCount() {
    linkedCount.value++;
  }

  function decrementLinkedCount() {
    if (linkedCount.value > 0) linkedCount.value--;
  }

  // TODO: re-enable when migrated to NOL
  // Fetch the linked-waiver count once the order reaches the completed step.
  // watch(
  //   isCompletedBooking,
  //   (completed) => {
  //     if (completed) {
  //       fetchLinkedWaiversCount();
  //     }
  //   },
  //   { immediate: true }
  // );

  return {
    showCancelTicketsModal,
    isLongTitle,
    iconContent,
    iconAlt,
    iconStyle,
    printIcon,
    printAlt,
    getCustomerFullName,
    onClickCustomers,
    calcVisibility,
    calcIcon,
    calcIconStyle,
    calcAlt,
    closeCalculator,
    DeleteIcon,
    closeModal,
    groupCurrency, // FIXME:
    isRemoveButtonLoading,
    copyToClipboard,
    showCopyCheck,
    printBill,
    showPrintBill,
    isCompletedBooking,
    isWaiverAllowed,
    linkedCount,
    isLoadingCount,
    incrementLinkedCount,
    decrementLinkedCount,
  };
}
